import {
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { firestore, auth } from '../config/firebase';

const COL = 'directConversations';
const MSG = 'messages';
const CLIENTS = 'clients';
const SHARES = 'shares';
const SHARE_PARTICIPANTS = 'participants';

/** @param {unknown} err */
function brokerClientChatError(err) {
  return Object.assign(err instanceof Error ? err : new Error(String(err)), {
    code: 'DIRECT_CHAT_BROKER_CLIENT',
  });
}

/** @param {string} a @param {string} b @returns {[string, string]} */
export function sortedParticipantUids(a, b) {
  return a < b ? [a, b] : [b, a];
}

export function brokerClientConversationId(clientId) {
  return `bc_${String(clientId)}`;
}

export function lenderFundInvestorConversationId(fundInvestorId) {
  return `lfi_${String(fundInvestorId)}`;
}

export function shareRecipientConversationId(shareToken, uidA, uidB) {
  const [low, high] = sortedParticipantUids(uidA, uidB);
  return `sr_${shareToken}_${low}_${high}`;
}

/** Recover share token from conversation id `sr_{token}_{uidLow}_{uidHigh}` (legacy rows may lack context). */
export function parseShareTokenFromShareRecipientConversationId(conversationId) {
  const id = String(conversationId || '');
  if (!id.startsWith('sr_')) return null;
  const rest = id.slice(3);
  if (rest.length < 34 || rest[32] !== '_') return null;
  const tok = rest.slice(0, 32);
  return /^[0-9a-f]{32}$/i.test(tok) ? tok : null;
}

/**
 * Meta for “go to deal” on share DMs: inbox snapshots sometimes omit `kind`/`context`; id always encodes the token.
 * @param {string} conversationId
 * @param {{ kind?: string, context?: Record<string, unknown> } | null | undefined} explicitMeta from UI / Firestore row
 * @param {{ kind?: string, context?: Record<string, unknown> } | null | undefined} ensureSpec
 */
export function resolveShareRecipientDirectMeta(conversationId, explicitMeta, ensureSpec) {
  let base = null;
  if (explicitMeta?.kind === 'share_recipient') {
    base = {
      kind: 'share_recipient',
      context:
        explicitMeta.context && typeof explicitMeta.context === 'object'
          ? { ...explicitMeta.context }
          : {},
    };
  } else if (ensureSpec?.kind === 'share_recipient') {
    base = {
      kind: 'share_recipient',
      context:
        ensureSpec.context && typeof ensureSpec.context === 'object' ? { ...ensureSpec.context } : {},
    };
  }
  const tokFromId = parseShareTokenFromShareRecipientConversationId(String(conversationId || ''));
  if (!tokFromId) return base;
  if (base && base.kind !== 'share_recipient') return base;
  const ctx = base?.context && typeof base.context === 'object' ? { ...base.context } : {};
  if (!String(ctx.shareToken || '').trim()) ctx.shareToken = tokFromId;
  return { kind: 'share_recipient', context: ctx };
}

/**
 * @param {string} clientId
 * @param {string} brokerUid
 * @param {string} clientUserId
 */
export function buildBrokerClientConversationSpec(clientId, brokerUid, clientUserId) {
  const cid = String(clientId);
  const [p0, p1] = sortedParticipantUids(String(brokerUid), String(clientUserId));
  return {
    conversationId: brokerClientConversationId(cid),
    participantUids: [p0, p1],
    kind: 'broker_client',
    context: { clientId: cid },
  };
}

/**
 * @param {string} fundInvestorId
 * @param {string} lenderUid
 * @param {string} investorUserId
 */
export function buildLenderFundInvestorConversationSpec(fundInvestorId, lenderUid, investorUserId) {
  const fid = String(fundInvestorId);
  const [p0, p1] = sortedParticipantUids(String(lenderUid), String(investorUserId));
  return {
    conversationId: lenderFundInvestorConversationId(fid),
    participantUids: [p0, p1],
    kind: 'lender_fundInvestor',
    context: { fundInvestorId: fid },
  };
}

/**
 * @param {string} shareToken
 * @param {string} uidA
 * @param {string} uidB
 */
export function buildShareRecipientConversationSpec(shareToken, uidA, uidB) {
  const tok = String(shareToken);
  const [p0, p1] = sortedParticipantUids(String(uidA), String(uidB));
  return {
    conversationId: shareRecipientConversationId(tok, uidA, uidB),
    participantUids: [p0, p1],
    kind: 'share_recipient',
    context: { shareToken: tok },
  };
}

/**
 * Find a share token where both UIDs have shares/{token}/participants/{uid} (required by Firestore for DM create).
 * Caller must be allowed to read both participant docs (typically the share owner / property owner).
 * @param {string} uidA
 * @param {string} uidB
 * @param {string[]} candidateTokens tried in order
 * @returns {Promise<string|null>}
 */
export async function findShareTokenForDirectChat(uidA, uidB, candidateTokens) {
  const a = String(uidA || '');
  const b = String(uidB || '');
  if (!a || !b) return null;
  const seen = new Set();
  const ordered = [];
  for (const tok of candidateTokens || []) {
    const t = String(tok || '').trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    ordered.push(t);
  }
  for (const tok of ordered) {
    try {
      const [snapA, snapB] = await Promise.all([
        getDoc(doc(firestore, SHARES, tok, SHARE_PARTICIPANTS, a)),
        getDoc(doc(firestore, SHARES, tok, SHARE_PARTICIPANTS, b)),
      ]);
      if (snapA.exists() && snapB.exists()) return tok;
    } catch {
      /* permission-denied or network */
    }
  }
  return null;
}

/**
 * Create conversation doc if missing (idempotent).
 * @param {{ participantUids: string[], kind: string, context: Record<string, string> }} spec
 */
export async function ensureDirectConversation(conversationId, spec) {
  if (!conversationId || !spec?.participantUids?.length) return;
  const ref = doc(firestore, COL, conversationId);
  const snap = await getDoc(ref);
  if (snap.exists()) return;

  let writeSpec = spec;
  if (spec.kind === 'broker_client') {
    const viewerUid = auth.currentUser?.uid;
    const clientId = spec.context?.clientId;
    if (!viewerUid) {
      throw brokerClientChatError(new Error('Not signed in'));
    }
    if (!clientId || typeof clientId !== 'string') {
      throw brokerClientChatError(new Error('Missing clientId'));
    }
    const cs = await getDoc(doc(firestore, CLIENTS, clientId));
    if (!cs.exists()) {
      throw brokerClientChatError(new Error('Client record not found'));
    }
    const c = cs.data() || {};
    const brokerUid = c.uid;
    const clientUserId = c.clientUserId;
    if (!brokerUid || !clientUserId) {
      throw brokerClientChatError(new Error('Client not linked to an account yet'));
    }
    if (viewerUid !== brokerUid && viewerUid !== clientUserId) {
      throw brokerClientChatError(new Error('Not a participant on this client record'));
    }
    const [e0, e1] = sortedParticipantUids(brokerUid, clientUserId);
    if (spec.participantUids[0] !== e0 || spec.participantUids[1] !== e1) {
      // users/{uid}/brokerLinks may have a stale brokerUid; Firestore truth is clients/{clientId}
      writeSpec = { ...spec, participantUids: [e0, e1] };
    }
  }

  try {
    await setDoc(ref, {
      participantUids: writeSpec.participantUids,
      kind: writeSpec.kind,
      context: writeSpec.context,
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
    });
  } catch (e) {
    if (spec.kind === 'broker_client' && e?.code === 'permission-denied') {
      throw brokerClientChatError(e);
    }
    throw e;
  }
}

/**
 * @param {string} conversationId
 * @param {(messages: Array<{ id: string } & Record<string, unknown>>) => void} onMessages
 * @param {(err: Error) => void} [onError]
 */
export function subscribeDirectMessages(conversationId, onMessages, onError) {
  if (!conversationId) return () => {};
  const q = query(
    collection(firestore, COL, conversationId, MSG),
    orderBy('createdAt', 'asc'),
    limit(300),
  );
  return onSnapshot(
    q,
    (snap) => onMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribeDirectMessages', err?.code, err?.message);
      onError?.(err);
    },
  );
}

/**
 * @param {string} conversationId
 * @param {string} authorUid
 * @param {string} authorDisplayName
 * @param {string} text
 * @param {string} [replyToId]
 */
export async function sendDirectMessage(conversationId, authorUid, authorDisplayName, text, replyToId) {
  const trimmed = (text || '').trim();
  if (!conversationId || !authorUid || !trimmed) return;
  const name = (authorDisplayName || '').trim().slice(0, 120);
  const payload = {
    authorUid,
    authorDisplayName: name,
    text: trimmed,
    createdAt: serverTimestamp(),
    type: 'user',
  };
  if (replyToId) payload.replyToId = replyToId;
  await addDoc(collection(firestore, COL, conversationId, MSG), payload);
  const preview = trimmed.length > 120 ? `${trimmed.slice(0, 120)}…` : trimmed;
  await updateDoc(doc(firestore, COL, conversationId), {
    lastMessageAt: serverTimestamp(),
    lastMessagePreview: preview,
    lastMessageAuthorUid: authorUid,
  });
}

/** Max conversations returned for the inbox query (most recent by lastMessage). No time TTL — older threads fall off only when more than this many have newer activity. */
export const DIRECT_CONVERSATIONS_INBOX_LIMIT = 40;

/**
 * @param {string} uid
 * @param {(rows: Array<{ id: string } & Record<string, unknown>>) => void} onList
 * @param {(err: Error) => void} [onError]
 * @param {number} [lim]
 */
export function subscribeDirectConversationsForUser(uid, onList, onError, lim = DIRECT_CONVERSATIONS_INBOX_LIMIT) {
  if (!uid) return () => {};
  const q = query(
    collection(firestore, COL),
    where('participantUids', 'array-contains', uid),
    orderBy('lastMessageAt', 'desc'),
    limit(lim),
  );
  return onSnapshot(
    q,
    (snap) => onList(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribeDirectConversationsForUser', err?.code, err?.message);
      onError?.(err);
    },
  );
}
